import { TextField as MuiTextField, TextFieldProps as MuiTextFieldProps, styled, useForkRef } from '@mui/material';
import React from 'react';

import { body, bodySmall, colorSystem, hoveredStyles } from '../../theme';
import { useFormItemLabelId } from '../forms/FormItem/FormItem';

export type TextFieldProps = Omit<MuiTextFieldProps, 'size'> & {
    size?: 'large' | 'small';
};

type HtmlInputSlotProps = NonNullable<MuiTextFieldProps['slotProps']>['htmlInput'];

type AriaName = { 'aria-label'?: string; 'aria-labelledby'?: string };

export const TextField = React.forwardRef(function TextField(
    { size = 'large', 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, ...props }: TextFieldProps,
    ref: React.ForwardedRef<HTMLDivElement>,
): JSX.Element {
    const rootRef = React.useRef<HTMLDivElement>(null);
    const handleRef = useForkRef(ref, rootRef);

    // MUI would put a top-level `aria-label` on its FormControl `div`, where it names nothing, so it goes to the input.
    const rootName: AriaName = { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy };
    // Inside a FormItem, the input is named by the FormItem's label unless it has a name of its own.
    const formItemLabelId = useFormItemLabelId(rootRef, !hasOwnName(props, rootName));
    const fallbackName = isNamed(rootName) ? rootName : { 'aria-labelledby': formItemLabelId };
    const slotProps = isNamed(fallbackName)
        ? { ...props.slotProps, htmlInput: withFallbackName(props.slotProps?.htmlInput, fallbackName) }
        : props.slotProps;

    switch (size) {
        case 'small':
            return (
                <SmallTextField ref={handleRef} {...props} slotProps={slotProps}>
                    {props.children}
                </SmallTextField>
            );
        case 'large':
            return (
                <LargeTextField ref={handleRef} {...props} slotProps={slotProps}>
                    {props.children}
                </LargeTextField>
            );
        default:
            return <MuiTextField ref={handleRef} {...props} slotProps={slotProps} />;
    }
});

function hasOwnName({ label, slotProps }: Omit<TextFieldProps, 'size'>, rootName: AriaName): boolean {
    const htmlInput = slotProps?.htmlInput;
    return (
        (label !== undefined && label !== null && label !== '') ||
        isNamed(rootName) ||
        // A function's result is checked when MUI calls it, in `withFallbackName`.
        (typeof htmlInput === 'object' && isNamed(htmlInput))
    );
}

function isNamed(props: object): boolean {
    const name = props as { 'aria-label'?: unknown; 'aria-labelledby'?: unknown };
    return Boolean(name['aria-label'] || name['aria-labelledby']);
}

/** Names the input by `name` unless `htmlInput` already names it. */
function withFallbackName(htmlInput: HtmlInputSlotProps, name: AriaName): HtmlInputSlotProps {
    if (typeof htmlInput === 'function') {
        return (ownerState) => {
            const resolved = htmlInput(ownerState);
            return isNamed(resolved) ? resolved : { ...resolved, ...name };
        };
    }
    return htmlInput !== undefined && isNamed(htmlInput) ? htmlInput : { ...htmlInput, ...name };
}

const DefaultTextField = styled(MuiTextField)({
    boxSizing: 'border-box',

    '& .MuiOutlinedInput-root': {
        padding: '0px',
        background: colorSystem.neutral.white,
        '& fieldset': {
            '&.MuiOutlinedInput-notchedOutline ': {
                border: `1px solid ${colorSystem.neutral[3]}`,
            },
        },
        '&:hover fieldset': {
            '&.MuiOutlinedInput-notchedOutline ': {
                ...hoveredStyles,
            },
        },

        '&.Mui-focused fieldset': {
            '&.MuiOutlinedInput-notchedOutline ': {
                border: `1px solid ${colorSystem.primary[5]}`,
                boxShadow: `0px 0px 0px 2px ${colorSystem.primary[3]}`,
            },
        },

        '&.Mui-disabled': {
            background: colorSystem.neutral[1],
            '& .MuiInputAdornment-root.MuiInputAdornment-positionStart': {
                '& .MuiTypography-root': {
                    color: colorSystem.neutral[5],
                },
            },
        },
        '&.Mui-disabled fieldset': {
            '&.MuiOutlinedInput-notchedOutline ': {
                border: `1px solid ${colorSystem.neutral[3]}`,
            },
        },
        '&.Mui-disabled:hover fieldset': {
            '&.MuiOutlinedInput-notchedOutline ': {
                border: `1px solid ${colorSystem.neutral[3]}`,
            },
        },
        '&.Mui-error fieldset': {
            '&.MuiOutlinedInput-notchedOutline ': {
                border: `1px solid ${colorSystem.red[5]}`,
            },
        },
        '&.Mui-error:hover fieldset': {
            '&.MuiOutlinedInput-notchedOutline ': {
                border: `1px solid ${colorSystem.red[5]}`,
            },
        },
    },

    '& .MuiOutlinedInput-multiline': {
        padding: 0,
    },

    '& .MuiInputBase-input': {
        color: colorSystem.neutral[9],
        '&::placeholder': {
            color: colorSystem.neutral[6],
            opacity: 1,
        },
        '&.Mui-disabled': {
            '&::placeholder': {
                color: colorSystem.neutral[5],
            },
        },
    },

    // Style the helper text
    '& .MuiFormHelperText-root': {
        color: colorSystem.neutral[7],
        margin: '4px 0 0 0',
    },
    '& .MuiFormHelperText-root.Mui-error': {
        color: colorSystem.red[7],
    },
    '& .MuiFormHelperText-root.Mui-disabled': {
        color: colorSystem.neutral[5],
    },

    // Style the start adornment
    '& .MuiInputAdornment-root.MuiInputAdornment-positionStart': {
        margin: '0 0 0 8px',
        padding: 0,
        '& .MuiTypography-root': {
            ...bodySmall,
            color: colorSystem.neutral[8],
            background: colorSystem.neutral[1],
            padding: '2px 4px',
            borderRadius: '4px',
            border: `1px solid ${colorSystem.neutral[2]}`,
        },
    },

    // Style the end adornment
    '& .MuiInputAdornment-root.MuiInputAdornment-positionEnd': {
        margin: '0 8px 0 0',
        padding: 0,
        '& .MuiTypography-root': {
            ...bodySmall,
            color: colorSystem.neutral[8],
            background: colorSystem.neutral[1],
            padding: '2px 4px',
            borderRadius: '4px',
            border: `1px solid ${colorSystem.neutral[2]}`,
        },
    },
});

const SmallTextField = styled(DefaultTextField)({
    '& .MuiOutlinedInput-input': {
        padding: '8px',
        height: '16px',
    },

    '& .MuiInputBase-input': {
        ...bodySmall,
        '&::placeholder': {
            ...bodySmall,
        },
    },
});

const LargeTextField = styled(DefaultTextField)({
    '& .MuiOutlinedInput-input': {
        padding: '10px 8px',
        height: '20px',
    },
    '& .MuiInputBase-input': {
        ...body,
        '&::placeholder': {
            ...body,
        },
    },
});
